import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse, PaginationInfo } from "./baseApi";

export interface Supplier {
  id: number;
  supplierCode: string;
  supplierName: string;
  faxNo: string;
  email: string;
  location: string;
  panNo: string;
  stateName: string;
  stateCode: string;
  contactNo: string;
  landLineNo: string;
  bankName: string;
  ifscCode: string;
  bankAccountNo: string;
  billAddress: string;
  shipAddress: string;
  gstNo: string;
  country: string;
  countryCurrency: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

class SupplierApiService extends BaseApiService {

  // Get all suppliers with pagination
  async getSuppliers(page: number = 1, pageSize: number = 10): Promise<ApiResponse<Supplier[]>> {
    try {
      // console.log('SupplierApiService: Making request to:', API_ENDPOINTS.SUPPLIERS.LIST);

      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<Supplier[]>>(`${API_ENDPOINTS.SUPPLIERS.LIST}?${params.toString()}`);
      
      // console.log('SupplierApiService: Response received:', response);
      return response;
    } catch (error) {
      // console.error('SupplierApiService: Error fetching suppliers:', error);
      toast.error('Failed to fetch suppliers');
      throw error;
    }
  }

  // Get supplier by ID
  async getSupplierById(id: string): Promise<ApiResponse<Supplier>> {
    try {
      const response = await this.makeRequest<ApiResponse<Supplier>>(API_ENDPOINTS.SUPPLIERS.DETAIL(id));
      return response;
    } catch (error) {
      toast.error('Failed to fetch supplier details');
      throw error;
    }
  }

  // Create new supplier
  async createSupplier(supplierData: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt' | 'supplierCode'>): Promise<ApiResponse<Supplier>> {
    try {
      const response = await this.makeRequest<ApiResponse<Supplier>>(API_ENDPOINTS.SUPPLIERS.CREATE, {
        method: HTTP_METHODS.POST,
        body: JSON.stringify(supplierData),
      });
      toast.success('Supplier created successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to create supplier');
      throw error;
    }
  }

  // Update supplier
  async updateSupplier(id: string, supplierData: Partial<Supplier>): Promise<ApiResponse<Supplier>> {
    try {
      // Coerce boolean isActive to numeric 1/0 for backend compatibility
      const bodyData: Record<string, unknown> = { ...supplierData };
      if (typeof bodyData.isActive === 'boolean') {
        bodyData.isActive = bodyData.isActive ? 1 : 0;
      }
      if (typeof bodyData.isActive === 'string') {
        const lower = (bodyData.isActive as string).toLowerCase();
        if (lower === 'true') bodyData.isActive = 1;
        if (lower === 'false') bodyData.isActive = 0;
      }

      const response = await this.makeRequest<ApiResponse<Supplier>>(API_ENDPOINTS.SUPPLIERS.UPDATE(id), {
        method: HTTP_METHODS.PUT,
        body: JSON.stringify(bodyData),
      });
      toast.success('Supplier updated successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to update supplier');
      throw error;
    }
  }

  // Delete supplier
  async deleteSupplier(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(API_ENDPOINTS.SUPPLIERS.DELETE(id), {
        method: HTTP_METHODS.DELETE,
      });
      toast.success('Supplier deleted successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to delete supplier');
      throw error;
    }
  }

  // Search suppliers with pagination
  async searchSuppliers(query: string, page: number = 1, pageSize: number = 10): Promise<ApiResponse<Supplier[]>> {
    try {
      const params = new URLSearchParams({
        keyword: query,
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<Supplier[]>>(`${API_ENDPOINTS.SUPPLIERS.SEARCH}?${params.toString()}`);
      
      return response;
    } catch (error) {
      toast.error('Failed to search suppliers');
      throw error;
    }
  }

  // Helper method to extract data from API response
  extractData<T>(response: ApiResponse<T>): T {
    if (!response.success) {
      throw new Error(response.message || 'API request failed');
    }
    return response.data;
  }

  // Helper method to get suppliers with extracted data
  async getSuppliersData(page: number = 1, pageSize: number = 10): Promise<{ suppliers: Supplier[], pagination: PaginationInfo }> {
    const response = await this.getSuppliers(page, pageSize);
    const suppliers = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: suppliers.length,
      pageSize: pageSize
    };
    
    return {
      suppliers,
      pagination
    };
  }

  // Helper method to search suppliers with extracted data
  async searchSuppliersData(query: string, page: number = 1, pageSize: number = 10): Promise<{ suppliers: Supplier[], pagination: PaginationInfo }> {
    const response = await this.searchSuppliers(query, page, pageSize);
    const suppliers = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: suppliers.length,
      pageSize: pageSize
    };
    
    return {
      suppliers,
      pagination
    };
  }
}

export const supplierApiService = new SupplierApiService(); 